import React, { useState, useEffect } from "react";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { Alert } from "../../../components/ui/alert";
import { useSystemTheme } from "../hooks/useSystemTheme";
import "../../sales-forecast/styles/theme.css";
import "./BathsControl.css"; // ⬅️ keep this for spinner visibility

export interface BathsControlProps {
  initialValue?: number;
  onChange: (value: number) => void;
  min?: number;
  max?: number;
  step?: number; // e.g., 0.5
}

/**
 * Themed numeric control for "Bathrooms".
 * Automatically adapts to system light/dark mode and matches plugin styling.
 */
export const BathsControl: React.FC<BathsControlProps> = ({
  initialValue = 2,
  onChange,
  min = 0,
  max = 10,
  step = 0.5,
}) => {
  const theme = useSystemTheme();

  // Pull colors dynamically from CSS variables
  const css = getComputedStyle(document.documentElement);
  const textColor = css.getPropertyValue("--text-color").trim();
  const borderColor = css.getPropertyValue("--border-color").trim();
  const inputBg = css.getPropertyValue("--input-bg").trim() || "var(--card-bg)";
  const labelColor = css.getPropertyValue("--muted-text-color").trim() || textColor;

  const [value, setValue] = useState<number>(initialValue);
  const [error, setError] = useState<string>("");

  // Keep synced with external resets
  useEffect(() => {
    setValue(initialValue);
  }, [initialValue]);

  // Validate and emit
  useEffect(() => {
    if (value < min) setError(`Must be ≥ ${min}`);
    else if (value > max) setError(`Must be ≤ ${max}`);
    else {
      setError("");
      onChange(value);
    }
  }, [value, min, max, onChange]);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const raw = e.target.value
      .replace(/[^0-9.]/g, "")
      .replace(/\.{2,}/g, ".")
      .replace(/^([^.]*)\.(.*)\./, "$1.$2"); // ensure only one decimal
    const num = parseFloat(raw);
    setValue(isNaN(num) ? 0 : num);
  };

  // Shared styles for theme consistency
  const inputClass =
    "w-full text-center rounded shadow-sm focus:outline-none [appearance:auto] transition-all duration-150";
  const inputStyle = {
    backgroundColor: inputBg,
    color: textColor,
    border: `1px solid ${borderColor}`,
  };

  const labelClass = "block text-sm font-medium mb-1";
  const labelStyle = { color: labelColor };

  return (
    <div
      className={`theme-${theme} space-y-2 transition-all duration-200`}
      style={{ color: textColor }}
    >
      <Label htmlFor="bathsInput" className={labelClass} style={labelStyle}>
        Bathrooms
      </Label>

      <Input
        id="bathsInput"
        type="number"
        inputMode="decimal"
        value={value.toString()}
        onChange={handleChange}
        step={step}
        min={min}
        max={max}
        className={inputClass}
        style={inputStyle}
      />

      {error && <Alert className="mt-1">{error}</Alert>}
    </div>
  );
};